import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Target, Users } from 'lucide-react';
import SEO from '../components/SEO';
import { FadeInUp } from '../components/Animations';

const AboutPage = () => {
  const values = [
    {
      icon: <Heart className="w-8 h-8" />,
      title: 'Compassionate Care',
      description: 'We approach every client with empathy, understanding, and genuine care for their wellbeing.'
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: 'Evidence-Based Practice',
      description: 'Our methods are grounded in the latest research and proven therapeutic approaches.'
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Neurodiversity-Affirming',
      description: 'We celebrate and support neurodivergent individuals with affirming, strength-based approaches.'
    }
  ];

  return (
    <>
      <SEO
        title="About Us - Expert Psychologists Specialising in ASD & ADHD"
        description="Learn about Navigating Minds Psychology. We're a team of experienced psychologists providing telehealth services with a focus on ASD/ADHD assessments and evidence-based therapy."
        keywords="about us, psychology practice, telehealth psychologists, ASD specialists, ADHD specialists, neurodiversity affirming"
        url="/about"
      />

      {/* Hero Section */}
      <section className="hero-gradient py-16 md:py-20 relative overflow-hidden">
        <div className="container mx-auto px-4">
          <FadeInUp>
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>
                About <span style={{ color: 'var(--blue-primary)' }}>Navigating Minds</span>
              </h1>
              <p className="text-lg md:text-xl" style={{ color: 'var(--text-secondary)' }}>
                Empowering individuals and families through professional, accessible telehealth psychology services
              </p>
            </div>
          </FadeInUp>
        </div>
      </section>

      {/* Our Story Section with Image */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <FadeInUp>
              <div>
                <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>
                  Our Story
                </h2>
                <div className="space-y-4 text-lg" style={{ color: 'var(--text-secondary)' }}>
                  <p>
                    Navigating Minds Psychology was founded with a clear mission: to make high-quality psychological services accessible to everyone, regardless of location. We believe that geographical barriers should never prevent someone from receiving the support they need.
                  </p>
                  <p>
                    Our practice specialises in ASD and ADHD assessments, recognising the critical importance of accurate diagnosis in unlocking appropriate support and understanding. We also provide comprehensive therapy services for individuals and families facing various mental health challenges.
                  </p>
                  <p>
                    Through secure telehealth technology, we bring professional psychological services directly to you—whether you're in a major city or a remote area of Australia. Our approach is warm, evidence-based, and neurodiversity-affirming.
                  </p>
                </div>
              </div>
            </FadeInUp>
            <FadeInUp delay={0.2}>
              <div className="relative">
                <img 
                  src="/images/about-consultation.png" 
                  alt="Professional psychology consultation"
                  className="rounded-2xl shadow-xl"
                />
              </div>
            </FadeInUp>
          </div>
        </div>
      </section>

      {/* Robyn Maddison Section */}
      <section className="section gray-bg">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <FadeInUp>
              <div className="relative">
                <img 
                  src="/images/robyn-photo.jpg" 
                  alt="Robyn Maddison - Child, Adolescent and Adult Psychologist"
                  className="rounded-2xl shadow-xl"
                />
                <div className="flex justify-center gap-6 mt-6">
                  <img 
                    src="/images/credentials.png" 
                    alt="AHPRA and AAPi Registered"
                    className="h-24 w-auto object-contain"
                  />
                </div>
              </div>
            </FadeInUp>
            <FadeInUp delay={0.2}>
              <div>
                <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
                  Robyn Maddison
                </h2>
                <h3 className="text-xl font-semibold mb-6" style={{ color: 'var(--blue-primary)' }}>
                  Child, Adolescent and Adult Psychologist
                </h3>
                <div className="space-y-4 text-base" style={{ color: 'var(--text-secondary)' }}>
                  <p>
                    Robyn is a highly skilled and compassionate Psychologist with extensive experience working across the lifespan, providing targeted support to children, adolescents, and adults facing diverse mental health challenges.
                  </p>
                  <p>
                    Robyn employs a person-centred and individualised approach, by emphasising the unique needs of each client, she strives to enhance the quality of life and promote a sense of enjoyment in the therapeutic journey. With a commitment to excellence in psychological care, Robyn provides expert intervention in the following areas:
                  </p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Anxiety & Depression</li>
                    <li>Autism Spectrum Disorders and Social Concerns</li>
                    <li>Behavioural Issues & ADHD</li>
                    <li>Trauma Backgrounds</li>
                    <li>Grief & Loss</li>
                  </ul>
                  <p>
                    Robyn works from various frameworks including Cognitive Behavioural Therapy (CBT), Acceptance and Commitment Therapy (ACT), Dialectic Behavioural Therapy (DBT), Mindfulness, Schema Therapy, Interpersonal Therapy, Narrative Approaches and Trauma-Based Interventions.
                  </p>
                  <div className="bg-[var(--blue-pale)] p-4 rounded-xl mt-6">
                    <h4 className="font-bold mb-2" style={{ color: 'var(--navy-primary)' }}>Qualifications</h4>
                    <p style={{ color: 'var(--text-secondary)' }}>
                      Robyn completed a Bachelor of Psychology with Honours and a Masters of Mental Health.
                    </p>
                  </div>
                </div>
              </div>
            </FadeInUp>
          </div>

          {/* Person Schema for Robyn Maddison (E-E-A-T) */}
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Person",
            "name": "Robyn Maddison",
            "jobTitle": "Child, Adolescent and Adult Psychologist",
            "worksFor": {
              "@type": "Organization",
              "name": "Navigating Minds Psychology"
            },
            "alumniOf": [
              {
                "@type": "EducationalOrganization",
                "name": "Bachelor of Psychology with Honours"
              },
              {
                "@type": "EducationalOrganization",
                "name": "Masters of Mental Health"
              }
            ],
            "knowsAbout": [
              "Autism Spectrum Disorder Assessment",
              "ADHD Assessment",
              "Cognitive Behavioural Therapy (CBT)",
              "Acceptance and Commitment Therapy (ACT)",
              "Dialectic Behavioural Therapy (DBT)",
              "Schema Therapy",
              "Trauma-Based Interventions",
              "Child Psychology",
              "Adolescent Psychology",
              "Adult Psychology"
            ],
            "hasOccupation": {
              "@type": "Occupation",
              "name": "Psychologist",
              "occupationalCategory": "Clinical Psychology",
              "qualifications": "Bachelor of Psychology (Honours), Masters of Mental Health",
              "responsibilities": "Providing psychological assessments (ASD, ADHD) and evidence-based therapy for children, adolescents, and adults"
            }
          })}} />
        </div>
      </section>

      {/* Values Section */}
      <section className="section gray-bg">
        <div className="container mx-auto px-4">
          <FadeInUp>
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
                Our Values
              </h2>
              <p className="text-lg max-w-2xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
                The principles that guide everything we do
              </p>
            </div>
          </FadeInUp>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {values.map((value, index) => (
              <FadeInUp key={value.title} delay={index * 0.1}>
                <div className="bg-white p-8 rounded-2xl shadow-md text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-[var(--blue-pale)] rounded-full mb-4" style={{ color: 'var(--blue-primary)' }}>
                    {value.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>
                    {value.title}
                  </h3>
                  <p style={{ color: 'var(--text-secondary)' }}>
                    {value.description}
                  </p>
                </div>
              </FadeInUp>
            ))}
          </div>
        </div>
      </section>

      {/* Telehealth Approach Section with Image */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <FadeInUp>
              <div className="relative lg:order-1">
                <img 
                  src="https://images.pexels.com/photos/6414715/pexels-photo-6414715.jpeg?auto=compress&cs=tinysrgb&w=800" 
                  alt="Telehealth therapy session"
                  className="rounded-2xl shadow-xl"
                />
              </div>
            </FadeInUp>
            <FadeInUp delay={0.2}>
              <div className="lg:order-2">
                <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>
                  Our Telehealth Approach
                </h2>
                <div className="space-y-4 text-lg" style={{ color: 'var(--text-secondary)' }}>
                  <p>
                    We're proud to offer fully online services that maintain the same high standards as traditional face-to-face psychology. Our telehealth platform is secure, user-friendly, and designed to create a comfortable therapeutic environment.
                  </p>
                  <p>
                    <strong>Benefits of our telehealth model:</strong>
                  </p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Access services from the comfort of your own space</li>
                    <li>No travel time or costs</li>
                    <li>Flexible scheduling to fit your life</li>
                    <li>Reduced anxiety for those who find in-person settings challenging</li>
                    <li>Available across all of Australia</li>
                  </ul>
                  <p>
                    Whether you're seeking an assessment or ongoing therapy, our telehealth approach ensures you receive the same quality care and professional attention you deserve.
                  </p>
                </div>
              </div>
            </FadeInUp>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section gray-bg">
        <div className="container mx-auto px-4">
          <FadeInUp>
            <div className="bg-[var(--blue-primary)] text-white rounded-3xl p-8 md:p-12 text-center max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Ready to Get Started?
              </h2>
              <p className="text-lg mb-8 opacity-90">
                Take the first step towards understanding and support.
              </p>
              <Link
                to="/contact"
                data-testid="about-cta-button"
                className="px-8 py-4 bg-white text-[var(--blue-primary)] rounded-full font-medium hover:bg-[var(--gray-100)] transition-all duration-300 hover:scale-105 inline-block"
              >
                Contact Us Today
              </Link>
            </div>
          </FadeInUp>
        </div>
      </section>
    </>
  );
};

export default AboutPage;